import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import {
  getLeaders,
  getLeaderAvailabilityByUser,
  getLeaderInterviews,
} from "../../services/leader-availabilities.service.ts";

interface ScheduleLeaderInterviewProps {
  interviewCheck: boolean;
  setInterviewCheck: (check: boolean) => void;
  selectedLeader: string;
  setSelectedLeader: (id: string) => void;
  selectedDate: Date | null;
  setSelectedDate: (date: Date | null) => void;
  selectedSlot: string;
  setSelectedSlot: (slot: string) => void;
}

const ScheduleLeaderInterview: React.FC<ScheduleLeaderInterviewProps> = ({
  interviewCheck,
  setInterviewCheck,
  selectedLeader,
  setSelectedLeader,
  selectedDate,
  setSelectedDate,
  selectedSlot,
  setSelectedSlot,
}) => {
  const [leaders, setLeaders] = useState<any[]>([]);
  const [leaderAvailabilities, setLeaderAvailabilities] = useState<any[]>([]);
  const [leaderInterviews, setLeaderInterviews] = useState<any[]>([]);
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Styles
  const cardStyle: React.CSSProperties = {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    padding: "24px",
    marginTop: "20px",
    border: "1px solid #e0e0e0",
  };

  const headerStyle: React.CSSProperties = {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#2d3748",
    marginBottom: "20px",
    borderBottom: "2px solid #f7fafc",
    paddingBottom: "10px",
  };

  const labelStyle: React.CSSProperties = {
    fontWeight: 500,
    color: "#4a5568",
    marginBottom: "8px",
    display: "block",
    fontSize: "0.9rem",
  };

  const inputContainerStyle: React.CSSProperties = {
    marginBottom: "16px",
  };

  const switchContainerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    marginBottom: interviewCheck ? "20px" : "0",
    cursor: "pointer",
  };

  // --- Logic ---

  // Fetch leaders when checkbox is active
  useEffect(() => {
    if (interviewCheck && leaders.length === 0) {
      const fetchLeaders = async () => {
        try {
          const data = await getLeaders();
          setLeaders(data || []);
        } catch (error) {
          console.error("Error fetching leaders:", error);
        }
      };
      fetchLeaders();
    }
  }, [interviewCheck]);

  // Fetch availability/interviews when leader changes
  useEffect(() => {
    if (!selectedLeader) {
      setLeaderAvailabilities([]);
      setLeaderInterviews([]);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [avails, interviews] = await Promise.all([
          getLeaderAvailabilityByUser(Number(selectedLeader)),
          getLeaderInterviews(Number(selectedLeader)),
        ]);
        setLeaderAvailabilities(avails || []);
        setLeaderInterviews(interviews || []);
        // Reset dependent fields only if not already match current selection (avoid loops if passed from parent but unlikely here)
        // Actually parent controls val, so we should enable parent to clear them if needed.
        // We will assume parent clears them or we clear them via props.
        // Note: The parent 'setSelectedDate' call inside useEffect might trigger loops if not careful.
        // Better to let user re-select or clear if leader changes.
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [selectedLeader]);

  // Calculate slots
  useEffect(() => {
    if (!selectedDate || !selectedLeader) {
      setAvailableSlots([]);
      return;
    }

    const dateStr = moment(selectedDate).format("YYYY-MM-DD");
    const dayAvails = leaderAvailabilities.filter((a) =>
      moment(a.startTime).isSame(dateStr, "day")
    );

    let slots: string[] = [];

    dayAvails.forEach((av) => {
      const start = moment(av.startTime);
      const end = moment(av.endTime);

      let current = start.clone();
      while (current.isBefore(end)) {
        const slotTime = current.format("HH:mm");

        const isTaken = leaderInterviews.some((interview) => {
          const intStart = moment(interview.startTime);
          const intEnd = moment(interview.endTime);
          const slotEnd = current.clone().add(15, "minutes");
          return current.isBefore(intEnd) && slotEnd.isAfter(intStart);
        });

        if (!isTaken) {
          slots.push(slotTime);
        }
        current.add(15, "minutes");
      }
    });

    slots = [...new Set(slots)].sort();
    setAvailableSlots(slots);
  }, [selectedDate, leaderAvailabilities, leaderInterviews]);

  // Handlers to clear downstream selections when upstream changes
  const handleLeaderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLeader(e.target.value);
    setSelectedDate(null);
    setSelectedSlot("");
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    setSelectedSlot("");
  };

  // Render day contents with color coding based on availability
  const renderDayContents = (day: number, date: Date) => {
    const isSelected =
      selectedDate && moment(date).isSame(moment(selectedDate), "day");
    const today = moment();
    const isToday = moment(date).isSame(today, "day");
    const isPast = moment(date).isBefore(today, "day");
    const formattedDate = moment(date).format("YYYY-MM-DD");
    const hasAvailability = leaderAvailabilities.some((a) =>
      moment(a.startTime).isSame(formattedDate, "day")
    );
    const isDisabled = !hasAvailability;

    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 6,
          backgroundColor: isSelected
            ? "rgba(37, 99, 235, 0.2)"
            : isToday && hasAvailability
            ? "rgba(34, 197, 94, 0.18)"
            : "transparent",
          color: isDisabled || isPast ? "#9ca3af" : "#111827",
          boxSizing: "border-box",
          opacity: isDisabled ? 0.5 : 1,
        }}
      >
        {day}
      </div>
    );
  };

  return (
    <div className="col-md- x-auto">
      <div
        style={switchContainerStyle}
        onClick={() => setInterviewCheck(!interviewCheck)}
      >
        <div className="form-check form-switch" style={{ marginRight: "10px" }}>
          <input
            className="form-check-input"
            type="checkbox"
            id="interviewCheck"
            checked={interviewCheck}
            onChange={(e) => setInterviewCheck(e.target.checked)}
            style={{ cursor: "pointer", width: "3em", height: "1.5em" }}
          />
        </div>
        <label
          className="form-check-label"
          htmlFor="interviewCheck"
          style={{
            fontWeight: 600,
            color: "#2d3748",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          ¿Agendar entrevista con Líder de Sucursal?
        </label>
      </div>

      {interviewCheck && (
        <div style={cardStyle} className="animate__animated animate__fadeIn">
          <div style={headerStyle}>
            <i
              className="fa fa-calendar-alt"
              style={{ marginRight: "10px", color: "#3182ce" }}
            ></i>
            Detalles de la Entrevista
          </div>

          <div className="row">
            {/* Leader Select */}
            <div className="col-md-12" style={inputContainerStyle}>
              <label style={labelStyle}>Seleccionar Líder</label>
              <select
                className="form-select fs-15px"
                value={selectedLeader}
                onChange={handleLeaderChange}
                style={{ borderRadius: "8px", padding: "10px" }}
              >
                <option value="">-- Seleccione un líder --</option>
                {leaders.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} {l.lastName} {l.branch ? `(${l.branch.name})` : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Select */}
            {selectedLeader && (
              <div className="col-md-6" style={inputContainerStyle}>
                <label style={labelStyle}>Fecha</label>
                <div style={{ position: "relative" }}>
                  <DatePicker
                    selected={selectedDate}
                    onChange={handleDateChange}
                    locale="es"
                    dateFormat="dd/MM/yyyy"
                    className="form-control fs-15px"
                    placeholderText="Seleccione fecha"
                    minDate={new Date()}
                    filterDate={(date: Date) => {
                      const dateStr = moment(date).format("YYYY-MM-DD");
                      return leaderAvailabilities.some((a) =>
                        moment(a.startTime).isSame(dateStr, "day")
                      );
                    }}
                    renderDayContents={renderDayContents}
                    popperPlacement="top-start"
                    wrapperClassName="w-100"
                  />
                  <i
                    className="fa fa-calendar-day"
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "12px",
                      color: "#a0aec0",
                      pointerEvents: "none",
                    }}
                  ></i>
                </div>
              </div>
            )}

            {/* Slot Select */}
            {selectedDate && (
              <div className="col-md-6" style={inputContainerStyle}>
                <label style={labelStyle}>Horario (15 min)</label>
                <select
                  className="form-select fs-15px"
                  value={selectedSlot}
                  onChange={(e) => setSelectedSlot(e.target.value)}
                  style={{ borderRadius: "8px", padding: "10px" }}
                  disabled={availableSlots.length === 0}
                >
                  <option value="">-- Seleccione hora --</option>
                  {availableSlots.length > 0 ? (
                    availableSlots.map((slot) => (
                      <option key={slot} value={slot}>
                        {slot}
                      </option>
                    ))
                  ) : (
                    <option disabled>Sin horarios disponibles</option>
                  )}
                </select>
                {availableSlots.length === 0 && (
                  <small className="text-danger mt-1 d-block">
                    No hay disponibilidad para este día.
                  </small>
                )}
              </div>
            )}

            {selectedSlot && (
              <div className="col-md-12 mt-2">
                <div
                  className="alert alert-info d-flex align-items-center"
                  role="alert"
                  style={{ borderRadius: "8px", fontSize: "0.9rem" }}
                >
                  <i className="fa fa-info-circle me-2"></i>
                  <div>
                    Se agendará una entrevista el{" "}
                    <strong>{moment(selectedDate).format("yyyy-MM-DD")}</strong>{" "}
                    a las <strong>{selectedSlot}</strong> con{" "}
                    <strong>
                      {leaders.find((l) => l.id == selectedLeader)?.name}
                    </strong>
                    .
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleLeaderInterview;
